import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { PageHeader } from "@/components/PageHeader";
import { RequirePermission } from "@/components/RequirePermission";
import { EmptyState, ErrorState, LoadingState } from "@/components/States";
import { adminStationAssignmentsApi } from "@/lib/api";
import { formatDateTime } from "@/lib/format";
import type { StationAssignmentReadDetailed, UUID } from "@/lib/types";

type Tab = "pending" | "active";

export default function StationAssignments() {
  const { t } = useTranslation();
  const [tab, setTab] = useState<Tab>("pending");
  const qc = useQueryClient();

  const pending = useQuery({
    queryKey: ["admin", "station-assignments", "pending"],
    queryFn: () => adminStationAssignmentsApi.listPending(),
    refetchInterval: 15_000,
  });
  const active = useQuery({
    queryKey: ["admin", "station-assignments", "active"],
    queryFn: () => adminStationAssignmentsApi.listActive(),
    refetchInterval: 15_000,
  });

  const approve = useMutation({
    mutationFn: ({ operatorId, stationId }: { operatorId: UUID; stationId: number }) =>
      adminStationAssignmentsApi.approve(operatorId, stationId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "station-assignments"] }),
  });
  const reject = useMutation({
    mutationFn: ({ operatorId, stationId }: { operatorId: UUID; stationId: number }) =>
      adminStationAssignmentsApi.reject(operatorId, stationId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "station-assignments"] }),
  });
  const revoke = useMutation({
    mutationFn: ({ operatorId, stationId }: { operatorId: UUID; stationId: number }) =>
      adminStationAssignmentsApi.revoke(operatorId, stationId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "station-assignments"] }),
  });

  return (
    <RequirePermission codes="station.assign">
      <PageHeader
        eyebrow={t("eyebrows.adminStationAssignments")}
        title={t("stationAssignments.title")}
        description={t("stationAssignments.description")}
      />

      <div className="flex items-center gap-2 border-b border-border mb-4">
        {(
          [
            ["pending", `${t("stationAssignments.tabPending")}${(pending.data?.length ?? 0) > 0 ? ` · ${pending.data!.length}` : ""}`],
            ["active", `${t("stationAssignments.tabActive")}${(active.data?.length ?? 0) > 0 ? ` · ${active.data!.length}` : ""}`],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            className={`px-4 py-2 text-sm font-mono uppercase tracking-wider border-b-2 -mb-px transition-colors ${
              tab === key
                ? "border-accent text-text-primary"
                : "border-transparent text-text-muted hover:text-text-secondary"
            }`}
            onClick={() => setTab(key)}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "pending" ? (
        pending.isLoading ? (
          <LoadingState />
        ) : pending.isError ? (
          <ErrorState error={pending.error} />
        ) : pending.data!.length === 0 ? (
          <EmptyState
            title={t("stationAssignments.noPending")}
            hint={t("stationAssignments.noPendingHint")}
          />
        ) : (
          <PendingTable
            items={pending.data!}
            onApprove={(op, st) => approve.mutate({ operatorId: op, stationId: st })}
            onReject={(op, st) => {
              if (confirm(t("stationAssignments.confirmReject"))) reject.mutate({ operatorId: op, stationId: st });
            }}
            busy={approve.isPending || reject.isPending}
          />
        )
      ) : active.isLoading ? (
        <LoadingState />
      ) : active.isError ? (
        <ErrorState error={active.error} />
      ) : active.data!.length === 0 ? (
        <EmptyState
          title={t("stationAssignments.noActive")}
          hint={t("stationAssignments.noActiveHint")}
        />
      ) : (
        <ActiveTable
          items={active.data!}
          onRevoke={(op, st, info) => {
            if (confirm(t("stationAssignments.confirmRevoke", { info }))) revoke.mutate({ operatorId: op, stationId: st });
          }}
          busy={revoke.isPending}
        />
      )}
    </RequirePermission>
  );
}

function PendingTable({
  items,
  onApprove,
  onReject,
  busy,
}: {
  items: StationAssignmentReadDetailed[];
  onApprove: (operatorId: UUID, stationId: number) => void;
  onReject: (operatorId: UUID, stationId: number) => void;
  busy: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div className="card overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-surface-alt">
          <tr className="text-left text-text-muted font-mono text-xs uppercase">
            <th className="px-4 py-3">{t("stationAssignments.colOperator")}</th>
            <th className="px-4 py-3">{t("stationAssignments.colRole")}</th>
            <th className="px-4 py-3">{t("stationAssignments.colStation")}</th>
            <th className="px-4 py-3">{t("stationAssignments.colRequested")}</th>
            <th className="px-4 py-3 text-right"></th>
          </tr>
        </thead>
        <tbody>
          {items.map((r) => (
            <tr
              key={`${r.operator_id}-${r.station_id}`}
              className="border-t border-border hover:bg-surface-hover"
            >
              <td className="px-4 py-3">
                <p>{r.operator_name}</p>
                {r.operator_login && (
                  <p className="text-text-muted font-mono text-xs mt-0.5">{r.operator_login}</p>
                )}
              </td>
              <td className="px-4 py-3">
                <span className="pill pill-accent font-mono text-xs">{r.operator_role}</span>
              </td>
              <td className="px-4 py-3">
                <p className="font-mono">{r.station_code}</p>
                <p className="text-text-muted text-xs mt-0.5">{r.station_name}</p>
              </td>
              <td className="px-4 py-3 text-text-muted text-xs">
                {formatDateTime(r.assigned_at)}
              </td>
              <td className="px-4 py-3 text-right space-x-2">
                <button
                  type="button"
                  className="btn btn-primary text-xs"
                  onClick={() => onApprove(r.operator_id, r.station_id)}
                  disabled={busy}
                >
                  {t("stationAssignments.approve")}
                </button>
                <button
                  type="button"
                  className="btn btn-danger text-xs"
                  onClick={() => onReject(r.operator_id, r.station_id)}
                  disabled={busy}
                >
                  {t("stationAssignments.reject")}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ActiveTable({
  items,
  onRevoke,
  busy,
}: {
  items: StationAssignmentReadDetailed[];
  onRevoke: (operatorId: UUID, stationId: number, info: string) => void;
  busy: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div className="card overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-surface-alt">
          <tr className="text-left text-text-muted font-mono text-xs uppercase">
            <th className="px-4 py-3">{t("stationAssignments.colOperator")}</th>
            <th className="px-4 py-3">{t("stationAssignments.colRole")}</th>
            <th className="px-4 py-3">{t("stationAssignments.colStation")}</th>
            <th className="px-4 py-3">{t("stationAssignments.colConfirmed")}</th>
            <th className="px-4 py-3 text-right"></th>
          </tr>
        </thead>
        <tbody>
          {items.map((r) => (
            <tr
              key={`${r.operator_id}-${r.station_id}`}
              className="border-t border-border hover:bg-surface-hover"
            >
              <td className="px-4 py-3">
                <p>{r.operator_name}</p>
                {r.operator_login && (
                  <p className="text-text-muted font-mono text-xs mt-0.5">{r.operator_login}</p>
                )}
              </td>
              <td className="px-4 py-3">
                <span className="pill pill-accent font-mono text-xs">{r.operator_role}</span>
              </td>
              <td className="px-4 py-3">
                <p className="font-mono">{r.station_code}</p>
                <p className="text-text-muted text-xs mt-0.5">{r.station_name}</p>
              </td>
              <td className="px-4 py-3 text-text-muted text-xs">
                {formatDateTime(r.assigned_at)}
              </td>
              <td className="px-4 py-3 text-right">
                <button
                  type="button"
                  className="btn btn-danger text-xs"
                  onClick={() =>
                    onRevoke(
                      r.operator_id,
                      r.station_id,
                      `${r.operator_name} от ${r.station_code}`,
                    )
                  }
                  disabled={busy}
                >
                  {t("stationAssignments.revoke")}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
